import { useId, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "catalyst-ui-kit/dialog";
import Notice from "components/tw/notice";
import {
  btnPrimaryCls,
  btnSecondaryCls,
  helpTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { sendInvoice } from "services/invoices.service";
import { formatPrettyDate } from "utils/helperFunctions";
import { invoiceErrorMessage } from "utils/invoice-errors";
import { formatInvoicePublicNumber } from "utils/invoice-number";
import type { InvoiceSendStatus } from "types/Invoice";

/** The fields of an invoice row (or loaded invoice) the dialog needs. */
export type SendableInvoice = {
  id: string;
  _number?: string | null;
  number?: string | number | null;
  status?: string | null;
  /** Prefilled recipient; leave out when the row does not carry it. */
  invoiceEmail?: string | null;
  send: InvoiceSendStatus;
};

type Props = {
  invoice: SendableInvoice;
  onClose: () => void;
  onSent: (send: InvoiceSendStatus) => void;
};

/** When and by whom an invoice already went out — the user must OK sending it again. */
type AlreadySent = { sentAt: string | null; sentBy?: string };

function alreadySentFrom(send: InvoiceSendStatus): AlreadySent | null {
  if (send.status !== "sent") return null;
  return { sentAt: send.lastSentAt, sentBy: send.lastSentBy?.name };
}

export default function SendInvoiceDialog({ invoice, onClose, onSent }: Props) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const recipientId = useId();
  const [to, setTo] = useState(invoice.invoiceEmail || "");
  // Known from the row, or learned from a 409 when someone sent it meanwhile.
  // Either way the next click is the user's go-ahead to send it again.
  const [alreadySent, setAlreadySent] = useState(alreadySentFrom(invoice.send));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!companyId) return;
    setSending(true);
    setError("");
    try {
      const result = await sendInvoice({
        companyId,
        invoiceId: invoice.id,
        to: to.trim() || undefined,
        force: !!alreadySent,
      });
      if (result.emailSent) {
        toast.success(
          t("invoice_send_done", {
            defaultValue: "Sent to {{to}}",
            to: result.emailTo,
          })
        );
      } else {
        toast.warning(
          t("invoice_send_not_accepted", {
            defaultValue: "The email could not be sent: {{reason}}",
            reason: result.emailError || "",
          })
        );
      }
      onSent(result.send);
    } catch (e: any) {
      const refusal = e?.response?.data;
      if (e?.response?.status === 409 && refusal?.code === "ALREADY_SENT") {
        setAlreadySent({ sentAt: refusal.sentAt, sentBy: refusal.sentBy?.name });
      } else {
        setError(invoiceErrorMessage(e, t));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    // data-portal-id: the invoice form opens this from inside a Polaris modal,
    // whose focus trap would otherwise pull focus out of the recipient field.
    <Dialog
      open
      onClose={() => !sending && onClose()}
      size="md"
      data-portal-id="send-invoice-dialog"
    >
      <DialogTitle>
        {t("invoice_send_title", {
          defaultValue: "Send invoice {{number}}",
          number: formatInvoicePublicNumber(invoice),
        })}
      </DialogTitle>
      <DialogBody>
        <div className="space-y-3">
          {alreadySent && (
            <Notice tone="warning">
              <p>
                {t("invoice_send_already_sent", {
                  defaultValue: "Already sent on {{date}}{{who}}. Send again?",
                  date: alreadySent.sentAt
                    ? formatPrettyDate(alreadySent.sentAt)
                    : "—",
                  who: alreadySent.sentBy ? ` (${alreadySent.sentBy})` : "",
                })}
              </p>
            </Notice>
          )}
          <div>
            <label htmlFor={recipientId} className={labelCls}>
              {t("invoice_send_recipient", { defaultValue: "Send to" })}
            </label>
            <input
              id={recipientId}
              type="text"
              className={inputCls}
              value={to}
              disabled={sending}
              onChange={(e) => setTo(e.target.value)}
            />
            <p className={helpTextCls}>
              {t("invoice_send_recipient_help", {
                defaultValue:
                  "Leave empty to use the customer's invoice email. Separate several addresses with a comma.",
              })}
            </p>
          </div>
          {error && (
            <Notice tone="critical">
              <p>{error}</p>
            </Notice>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <button
          type="button"
          className={btnSecondaryCls}
          onClick={onClose}
          disabled={sending}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className={btnPrimaryCls}
          onClick={submit}
          disabled={sending}
        >
          {sending
            ? t("sending", { defaultValue: "Sending…" })
            : alreadySent
            ? t("invoice_send_again", { defaultValue: "Send again" })
            : t("send", { defaultValue: "Send" })}
        </button>
      </DialogActions>
    </Dialog>
  );
}
